import { getContext, setContext } from 'svelte';
import { derived, type Readable } from 'svelte/store';
import {
  createMutation,
  createQuery,
  QueryClient,
} from '$lib/api/tanstack-query';

import type * as APITypes from '@viamrobotics/web-client/user/v1/user';
import type { InternalUserServiceClient } from '@viamrobotics/web-client/user/v1/user.client';

import { type AuthContext, AuthStatus, useAuthContext } from '$lib/auth';

import { useInternalUserClient } from './client';
import { useQueryInvalidator } from './invalidate';
import { prefetchQuery } from './prefetch';
import {
  type BaseQueryOptions,
  createQueryOptions,
  getAuthedQueryOptions,
} from './query-options';

const UserContextKey = Symbol('user');

/** User state and actions. */
export interface UserContext {
  user: Readable<APITypes.User | undefined>;
  userID: Readable<string | undefined>;
}

/** Provide current user state and actions to the component tree. */
export const provideUserContext = (): void => {
  const appUserQuery = createGetAppUserQuery();
  const user = derived(appUserQuery, getAppUser);
  const userID = derived(appUserQuery, ($query) => getAppUser($query)?.id);

  setContext<UserContext>(UserContextKey, {
    user,
    userID,
  });
};

/**
 * Use user state and actions.
 *
 * @returns user state and actions.
 */
export const useUserContext = (): UserContext => {
  return getContext<UserContext>(UserContextKey);
};

const getAppUserQueryOptions = ({
  client,
  getRPCOptions,
  enabled,
}: BaseQueryOptions<InternalUserServiceClient>) => {
  return createQueryOptions({
    queryKey: ['user'],
    enabled,
    queryFn: async () => {
      const rpcOptions = await getRPCOptions();
      const { response } = await client.getAppUser({}, rpcOptions);

      return response;
    },
  });
};

const createGetAppUserQuery = () => {
  const userClient = useInternalUserClient();
  const { authState, getRPCOptions } = useAuthContext();

  const queryOptions = derived([authState], ([$authState]) =>
    getAppUserQueryOptions({
      client: userClient,
      getRPCOptions,
      enabled: $authState.status === AuthStatus.LoggedIn,
      user:
        $authState.status === AuthStatus.LoggedIn ? $authState.user : undefined,
    })
  );
  return createQuery(queryOptions);
};

export const prefetchAppUser = async (
  queryClient: QueryClient,
  client: InternalUserServiceClient,
  authContext: AuthContext
) => {
  const baseOptions = getAuthedQueryOptions(client, authContext);
  const options = getAppUserQueryOptions(baseOptions);
  return await prefetchQuery(queryClient, options);
};

/** Data from an RPC API call to GetAppUser. */
export interface GetAppUserQuery {
  data: APITypes.GetAppUserResponse | undefined;
}

export const getAppUser = (
  appUserQuery: GetAppUserQuery
): APITypes.User | undefined => {
  return appUserQuery.data?.user;
};

export const useCreateSetUserOrgLastAccessMutation = () => {
  const { invalidateQueries } = useQueryInvalidator();
  const userClient = useInternalUserClient();
  const { getRPCOptions } = useAuthContext();
  return createMutation({
    mutationFn: async (request: APITypes.SetUserOrgLastAccessRequest) => {
      const rpcOptions = await getRPCOptions();
      const { response } = await userClient.setUserOrgLastAccess(
        request,
        rpcOptions
      );
      return response;
    },
    onSuccess: async () => {
      await invalidateQueries([['user']]);
    },
  });
};
